// 6-box verification code input for the submission flow.
//
// New component (the legacy Common/CodeInput is left untouched). Differences
// that matter here: paste sanitizing ("code: 123 456" → "123456"), OTP
// autofill hints for iOS/Android keyboards, auto-submit on the 6th digit, and
// translate="no" so browser auto-translate cannot rewrite the boxes out from
// under React.

import React from "react";
import i18next from "i18next";
import VerificationInput from "react-verification-input";
import styled, { keyframes } from "styled-components";
import BusyLabel from "./BusyLabel";

interface Props {
  length?: number;
  value: string;
  onChange: (code: string) => void;
  onComplete: (code: string) => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
  // The code is in and being checked: green boxes and "Checking…" at once, instead of boxes
  // that only grey out for the whole wait.
  isChecking?: boolean;
  ariaLabel?: string;
}

// Keeps digits only and caps at `length` — handles "Your code: 123 456",
// pasted codes with hyphens, and clipboard text carrying extra numbers.
// The answers that mean "this code will never work": the boxes empty for a new one.
// Anything else (a network error, a reCAPTCHA hiccup, an unexpected failure) keeps
// the code, so Enter can try it again.
const DEFINITIVE_CODE_ERRORS = ["invalidCode", "codeExpired", "codeReplaced", "tooManyAttempts"];
export const shouldClearCode = (errorCode: string | null | undefined): boolean =>
  !!errorCode && DEFINITIVE_CODE_ERRORS.indexOf(errorCode) >= 0;

export const sanitizeCode = (raw: string, length: number): string =>
  (raw || "").replace(/[^0-9]/g, "").slice(0, length);

const SubmissionCodeInput: React.FC<Props> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  isInvalid,
  isDisabled,
  isChecking,
  ariaLabel,
}) => {
  const handleChange = (raw: string) => {
    const code = sanitizeCode(raw, length);
    onChange(code);
    if (code.length === length) {
      // Blur so the mobile keyboard drops before the success transition.
      try {
        const active = document.activeElement as HTMLElement;
        if (active && active.blur) active.blur();
      } catch (e) {
        // ignore
      }
      onComplete(code);
    }
  };

  // Enter with every box filled checks the code again (e.g. after a network error),
  // unless the input is disabled or a check is already running.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" || (e.nativeEvent as any).isComposing) return;
    if (isDisabled || isChecking || sanitizeCode(value, length).length !== length) return;
    e.preventDefault();
    onComplete(sanitizeCode(value, length));
  };

  return (
    <Wrapper
      onKeyDown={handleKeyDown}
      isInvalid={!!isInvalid}
      isDisabled={!!isDisabled}
      isChecking={!!isChecking && !isInvalid}
      // `notranslate` + translate="no" keep browser auto-translate from
      // rewriting the code boxes and breaking React's reconciliation.
      // (styled-components v3 typings omit `translate`, hence the cast.)
      className="notranslate"
      {...({ translate: "no" } as any)}
    >
      <VerificationInput
        autoFocus
        length={length}
        value={value}
        onChange={handleChange}
        validChars={"0-9"}
        // Empty boxes, as in Figma 4 — the library's default "·" in each read
        // as six characters already typed.
        placeholder=""
        inputProps={{
          autoComplete: "one-time-code",
          inputMode: "numeric",
          pattern: "[0-9]*",
          disabled: isDisabled,
          "aria-label": ariaLabel || i18next.t("Enter the 6 digit verification code"),
        }}
      />
      {isChecking && !isInvalid && (
        <BusyLabel>{i18next.t("Checking…")}</BusyLabel>
      )}
    </Wrapper>
  );
};

const cursorAnimation = keyframes`
  0%, 50% { background-color: #777; }
  50.00001%, 100% { background-color: inherit; }
`;

const Wrapper = styled.div<{ isInvalid: boolean; isDisabled: boolean; isChecking: boolean }>`
  margin: 0 auto 12px;
  position: relative;

  & .vi__container {
    margin: 0 auto;
    justify-content: center;
    height: 50px;
    gap: 8px;
  }

  & .vi__character {
    cursor: ${(p) => (p.isDisabled ? "default" : "text")};
    max-width: 44px;
    border-radius: 4px;
    border: 1px solid
      ${(p) => (p.isInvalid ? "#c7053f" : p.isChecking ? "#0bb07b" : p.isDisabled ? "#e3e8ee" : "#c9d3e0")};
    background-color: ${(p) => (p.isChecking ? "#effaf5" : "#f7f9fb")};
    color: ${(p) => (p.isChecking ? "#122640" : p.isDisabled ? "#8a94a6" : "#122640")};
    transition: border-color 0.15s, background-color 0.15s;
    font-size: 18px;
    line-height: 48px;

    &--selected {
      outline-color: #0f9bf0;
      border-color: #0f9bf0;
    }

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 50%;
      background-color: #f7f9fb;
      transform: translate(-50%, -50%);
    }
    &--selected::after {
      animation: ${cursorAnimation} 1s infinite;
    }
    &--filled::after {
      opacity: 0;
    }
  }
`;

export default SubmissionCodeInput;
